import React from 'react';
import { IoChevronForward } from 'react-icons/io5';
import { Icon } from '../Icon';
import style from './styles.module.css';

type ButtonSize = 'sm' | 'md' | 'lg' | 'none';
type LegacyButtonSize = 'small' | 'medium' | 'large';
type ButtonSizeOption = ButtonSize | LegacyButtonSize;

const SIZE_CLASSNAME: Record<ButtonSizeOption, ButtonSize> = {
  sm: 'sm',
  small: 'sm',
  md: 'md',
  medium: 'md',
  lg: 'lg',
  large: 'lg',
  none: 'none',
};

type VisualVariant = 'solid' | 'outline';
type SpecialVariant = 'text' | 'icon' | 'pagination';
type LegacyVariant = 'default' | 'primary' | 'error';
type ButtonVariant = VisualVariant | SpecialVariant | LegacyVariant;
type NormalizedVariant = VisualVariant | SpecialVariant;
type ButtonTone = 'primary' | 'secondary' | 'error';

const LEGACY_VARIANT_MAP: Record<
  LegacyVariant,
  { variant: VisualVariant; tone: ButtonTone }
> = {
  default: { variant: 'solid', tone: 'primary' },
  primary: { variant: 'solid', tone: 'primary' },
  error: { variant: 'solid', tone: 'error' },
};

const VISUAL_VARIANT_TONES: Record<VisualVariant, Readonly<ButtonTone[]>> = {
  solid: ['primary', 'secondary', 'error'],
  outline: ['primary', 'error'],
};

const isLegacyVariant = (variant: ButtonVariant): variant is LegacyVariant =>
  variant === 'default' || variant === 'primary' || variant === 'error';

const isVisualVariant = (
  variant: NormalizedVariant
): variant is VisualVariant => variant === 'solid' || variant === 'outline';

const normalizeVariant = (variant: ButtonVariant): NormalizedVariant => {
  if (isLegacyVariant(variant)) {
    return LEGACY_VARIANT_MAP[variant].variant;
  }

  return variant as NormalizedVariant;
};

const resolveTone = (
  normalizedVariant: NormalizedVariant,
  requestedTone?: ButtonTone,
  fallbackVariant?: ButtonVariant
): ButtonTone => {
  if (!isVisualVariant(normalizedVariant)) {
    return 'primary';
  }

  const allowedTones = VISUAL_VARIANT_TONES[normalizedVariant];
  if (requestedTone && allowedTones.includes(requestedTone)) {
    return requestedTone;
  }

  if (fallbackVariant && isLegacyVariant(fallbackVariant)) {
    const legacyTone = LEGACY_VARIANT_MAP[fallbackVariant].tone;
    if (allowedTones.includes(legacyTone)) {
      return legacyTone;
    }
  }

  return allowedTones[0];
};

const getVariantClassName = (
  variant: NormalizedVariant,
  tone: ButtonTone
): string | undefined => {
  if (!isVisualVariant(variant)) {
    return style[variant];
  }

  const toneKey = `${variant}${tone[0].toUpperCase()}${tone.slice(
    1
  )}` as keyof typeof style;
  return style[toneKey];
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  backgroundColor?: string;
  size?: ButtonSizeOption;
  tone?: ButtonTone;
  label?: string;
  icon?: React.ReactNode;
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
}

export const Button = ({
  variant = 'solid',
  tone,
  size = 'md',
  backgroundColor,
  label,
  icon,
  iconStart,
  iconEnd,
  ...props
}: ButtonProps) => {
  const normalizedSize = SIZE_CLASSNAME[size] ?? 'md';
  const normalizedVariant = normalizeVariant(variant);
  const resolvedTone = resolveTone(normalizedVariant, tone, variant);
  const variantClassName = getVariantClassName(normalizedVariant, resolvedTone);

  const defaultIconEnd =
    !iconEnd && ['solid', 'outline'].includes(normalizedVariant) ? (
      <Icon icon={IoChevronForward} aria-hidden="true" />
    ) : null;

  return (
    <button
      className={`${style.button} ${style[normalizedSize]} ${
        variantClassName ?? ''
      }`}
      style={{ backgroundColor }}
      {...props}
    >
      {['icon', 'pagination'].includes(variant) ? (
        <>{icon && <span className={`${style.buttonIcon}`}>{icon}</span>}</>
      ) : (
        <>
          {iconStart && <span className={style.buttonIcon}>{iconStart}</span>}
          {label}
          {(iconEnd || defaultIconEnd) && (
            <span className={style.buttonIcon}>
              {iconEnd || defaultIconEnd}
            </span>
          )}
        </>
      )}
    </button>
  );
};
